import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { UpiPaymentModal } from "../components/UpiPaymentModal";
import { Loader2, Lock, PlayCircle, FileText, CheckCircle2, Layers, Clock, User } from "lucide-react";
import { toast } from "sonner";

export default function CourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [upiConfig, setUpiConfig] = useState(null);
  const [showUpi, setShowUpi] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [pendingPayment, setPendingPayment] = useState(null);

  const load = useCallback(() => {
    api.get(`/courses/${id}`).then((res) => setCourse(res.data));
  }, [id]);

  useEffect(() => {
    load();
    api.get("/upi-config").then((res) => setUpiConfig(res.data));
    if (user) {
      api.get("/me/payments").then((res) => {
        const p = res.data.find((x) => x.course_id === id && x.status === "pending");
        setPendingPayment(p || null);
      });
    }
  }, [id, user, load]);

  const handleEnrollFree = async () => {
    if (!user) return navigate("/login", { state: { from: `/courses/${id}` } });
    setEnrolling(true);
    try {
      await api.post(`/courses/${id}/enroll`);
      toast.success("Enrolled! Start learning now.");
      navigate(`/learn/${id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setEnrolling(false);
    }
  };

  const handleBuy = () => {
    if (!user) return navigate("/login", { state: { from: `/courses/${id}` } });
    setShowUpi(true);
  };

  if (!course)
    return (
      <div className="min-h-screen bg-[#FAFAFC]"><Navbar />
        <div className="grid place-items-center py-32"><Loader2 className="h-8 w-8 animate-spin text-teal-600" /></div>
      </div>
    );

  const totalLessons = course.modules?.reduce((s, m) => s + m.lessons.length, 0) || 0;

  return (
    <div className="min-h-screen bg-[#FAFAFC]">
      <Navbar />

      {/* Hero */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <span className="text-xs font-semibold uppercase tracking-widest text-teal-400">{course.category}</span>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-3" style={{ fontFamily: "Outfit, sans-serif" }}>{course.title}</h1>
            <p className="mt-4 text-slate-300 max-w-2xl leading-relaxed">{course.description}</p>
            <div className="flex flex-wrap gap-5 mt-6 text-sm text-slate-300">
              <span className="flex items-center gap-1.5"><User className="h-4 w-4 text-teal-400" /> {course.instructor}</span>
              <span className="flex items-center gap-1.5"><Layers className="h-4 w-4 text-teal-400" /> {course.modules?.length || 0} modules</span>
              <span className="flex items-center gap-1.5"><PlayCircle className="h-4 w-4 text-teal-400" /> {totalLessons} lessons</span>
              <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-teal-400" /> {course.level}</span>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="rounded-2xl bg-white text-slate-900 overflow-hidden shadow-xl">
              <img src={course.cover_image} alt={course.title} className="h-40 w-full object-cover" />
              <div className="p-6">
                {course.is_free ? (
                  <p className="text-3xl font-extrabold text-emerald-600" style={{ fontFamily: "Outfit, sans-serif" }}>Free</p>
                ) : (
                  <p className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>₹{course.price}</p>
                )}

                <div className="mt-5">
                  {course.enrolled ? (
                    <Button data-testid="course-continue-button" onClick={() => navigate(`/learn/${id}`)} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white h-11">
                      <CheckCircle2 className="h-4 w-4 mr-1.5" /> Go to Course
                    </Button>
                  ) : pendingPayment ? (
                    <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-center">
                      <p className="text-sm font-semibold text-amber-700 flex items-center justify-center gap-1.5"><Clock className="h-4 w-4" /> Payment Pending Approval</p>
                      <p className="text-xs text-amber-600 mt-1">UTR: {pendingPayment.utr}</p>
                    </div>
                  ) : course.is_free ? (
                    <Button data-testid="course-enroll-button" onClick={handleEnrollFree} disabled={enrolling} className="w-full bg-teal-700 hover:bg-teal-600 text-white h-11">
                      {enrolling ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enroll for Free"}
                    </Button>
                  ) : (
                    <Button data-testid="course-enroll-button" onClick={handleBuy} className="w-full bg-amber-500 hover:bg-amber-400 text-white h-11">
                      Buy via UPI · ₹{course.price}
                    </Button>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-3 text-center">Video lessons + downloadable PDF notes</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Curriculum */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: "Outfit, sans-serif" }}>Course Content</h2>
        <div className="space-y-4 max-w-3xl">
          {course.modules?.map((m, mi) => (
            <div key={m.id} className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
              <div className="px-5 py-4 bg-slate-50 border-b border-slate-200">
                <h3 className="font-semibold text-slate-900">Module {mi + 1}: {m.title}</h3>
              </div>
              <div className="divide-y divide-slate-100">
                {m.lessons.map((l) => (
                  <div key={l.id} className="px-5 py-3.5 flex items-center gap-3">
                    {l.locked ? <Lock className="h-4 w-4 text-slate-400 shrink-0" /> : <PlayCircle className="h-4 w-4 text-teal-600 shrink-0" />}
                    <span className={`text-sm ${l.locked ? "text-slate-500" : "text-slate-800"}`}>{l.title}</span>
                    {l.pdf_name && <FileText className="h-3.5 w-3.5 text-amber-500" />}
                    <span className="ml-auto text-xs text-slate-400">{l.duration}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {upiConfig && <UpiPaymentModal open={showUpi} onClose={() => setShowUpi(false)} course={course} upiConfig={upiConfig} onSubmitted={() => { load(); api.get("/me/payments").then((res) => setPendingPayment(res.data.find((x) => x.course_id === id && x.status === "pending") || null)); }} />}
    </div>
  );
}
